/**
 * Sprint 5-b PWA · Standalone 專屬行動版 layout（D20/D21 改版）
 *
 * - 取代桌機 MainLayout（sidebar + topnav），讓 PWA「自成一個 app」。
 * - 頁首（漸層 --header-bg-start/end）：租戶 logo + 名稱 + 模式切換（功能/聊天）+ 頭像選單；
 *   非 /ee_pwa_home 時 logo 左顯「←」返回首頁。
 * - 不要底部導覽列（D21）：各功能頁左上「←」回首頁；頁尾顯版權。
 * - 頁面高度 100dvh（+@supports 退回 100vh·避免手機網址列裁切）。
 * - 保留 session heartbeat（避免 PWA 使用中 session 過期）。
 */
import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  AppBar, Avatar, Box, IconButton, Menu, MenuItem,
  ListItemIcon, ListItemText, Toolbar, Typography, alpha,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LogoutIcon from '@mui/icons-material/Logout';

import { useAuth } from '../../../../contexts/AuthContext';
import { useSystem } from '../../../../contexts/SystemContext';
import { useSessionHeartbeat } from '../../../../hooks/useSessionHeartbeat';
import LanguageSwitcher from '../../../../components/LanguageSwitcher';
import ModeSwitcher from '../../../../components/ModeSwitcher';

const HOME = '/ee_pwa_home';

const MobileLayout: React.FC = () => {
  useSessionHeartbeat();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const tt = (k: string, d: string) => t(`pwaLayout.${k}`, d);
  const { user, logout } = useAuth();
  const { tenantBranding, getSystemTitle, getCopyright } = useSystem();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const logoSrc = tenantBranding?.logo_file_id
    ? `/api/system/branding-image/${tenantBranding.logo_file_id}`
    : '/logo.png';
  const title = getSystemTitle() || 'WASIR';
  const atHome = pathname === HOME;

  const doLogout = async () => {
    setAnchor(null);
    try { await logout(); } finally { nav('/login'); }
  };

  return (
    <Box sx={{
      height: '100vh', '@supports (height: 100dvh)': { height: '100dvh' },
      bgcolor: 'grey.200', display: 'flex', justifyContent: 'center',
    }}>
      <Box sx={{
        width: '100%', maxWidth: 500, height: '100%',
        bgcolor: 'background.default', display: 'flex', flexDirection: 'column',
        boxShadow: { xs: 0, sm: 3 },
      }}>
        {/* 頁首（漸層·safe-area-inset-top）*/}
        <AppBar position="sticky" elevation={1}
          sx={{
            paddingTop: 'env(safe-area-inset-top)',
            background: 'linear-gradient(135deg, var(--header-bg-start, #13386B) 0%, var(--header-bg-end, #1a4a8a) 100%)',
            color: 'primary.contrastText',
          }}>
          <Toolbar variant="dense" sx={{ gap: 1 }}>
            {!atHome && (
              <IconButton size="small" edge="start" onClick={() => nav(HOME)}
                aria-label={tt('home', '首頁')} sx={{ color: 'primary.contrastText', mr: 0.5 }}>
                <ArrowBackIcon />
              </IconButton>
            )}
            <Box component="img" src={logoSrc} alt="logo" sx={{ height: 28, width: 'auto' }} />
            <Typography variant="subtitle1" noWrap sx={{ flex: 1, fontWeight: 600, minWidth: 0 }}>{title}</Typography>
            <ModeSwitcher variant="onPrimary" />
            <LanguageSwitcher variant="dark" />
            <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label={tt('account', '帳號')}>
              <Avatar sx={{ width: 30, height: 30, fontSize: 14,
                bgcolor: (theme) => alpha(theme.palette.primary.contrastText, 0.25),
                color: 'primary.contrastText' }}>
                {(user?.username || '?').charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
              <MenuItem disabled>
                <ListItemText primary={user?.username || ''} secondary={user?.account || ''} />
              </MenuItem>
              <MenuItem onClick={doLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
                <ListItemText primary={tt('logout', '登出')} />
              </MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>

        {/* 內容區 */}
        <Box sx={{ flex: 1, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Outlet />
        </Box>

        {/* 頁尾版權（safe-area-inset-bottom）*/}
        <Box component="footer" sx={{
          flexShrink: 0, textAlign: 'center', py: 1,
          paddingBottom: 'calc(8px + env(safe-area-inset-bottom, 0px))',
          borderTop: 1, borderColor: 'divider',
        }}>
          <Typography variant="caption" color="text.secondary">
            {getCopyright() || tt('copyright', 'Copyright © 2026')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default MobileLayout;
